import request from "../../utils/request";
import { Result } from "../../models/general";

// AI Settings types
export interface AISettingsView {
  companyId: number;
  companyName: string;
  aiFeaturesEnabled: boolean;
  hasCustomAIKey: boolean;
  azureOpenAIEndpoint: string | null;
  azureOpenAIChatDeployment: string | null;
}

export interface AISettingsInput {
  aiFeaturesEnabled?: boolean | null;
  azureOpenAIEndpoint?: string | null;
  azureOpenAIApiKey?: string | null;
  azureOpenAIChatDeployment?: string | null;
  clearAIKey?: boolean;
}

/**
 * Get AI settings for all companies (Operator only)
 */
export async function getCompaniesAISettings() {
  const response = await request.get<Result<AISettingsView[]>>("AISettings/Companies");
  return response.data;
}

/**
 * Get AI settings for a specific company (Operator only)
 */
export async function getCompanyAISettings(companyId: number) {
  const response = await request.get<Result<AISettingsView>>(`AISettings/${companyId}`);
  return response.data;
}

/**
 * Update AI settings for a specific company (Operator only)
 */
export async function updateCompanyAISettings(companyId: number, input: AISettingsInput) {
  const response = await request.put<Result<AISettingsView>>(`AISettings/${companyId}`, input);
  return response.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// CO-355 — per-company DAILY AI QUOTA.
//
// Two surfaces, deliberately asymmetric:
//   * `AISettings/Quota/*` — OPERATOR only, read AND write. A usage quota is a commercial lever
//     (owner decision E11, 2026-08-20).
//   * `AiQuota/MyCompany`  — HR/Admin, READ ONLY. There is no write counterpart on the server, so
//     there is nothing for this file to call even if a future screen wanted one.
//
// 🔴 TRISTATE: every `override*` field is `number | null` / `boolean | null` where **null means NOT
// CONFIGURED** and the company inherits the platform value. Null is never "zero" and never
// "unlimited" — render `platform*` as the placeholder beside an empty field so the distinction is
// visible, and send `null` (not `0`, not `undefined`) to clear an override.
// ─────────────────────────────────────────────────────────────────────────────

export interface AiQuotaConfigView {
  companyId: number;
  companyName: string;

  /** null = not configured, inherits the platform value below. */
  overrideEnabled: boolean | null;
  overrideHrAdmin: number | null;
  overrideChiefManager: number | null;
  overrideEmployee: number | null;
  note: string | null;
  hasOverride: boolean;

  /** The compiled platform default (server AiQuotaOptions). The fallback for every null above. */
  platformEnabled: boolean;
  platformHrAdmin: number;
  platformChiefManager: number;
  platformEmployee: number;

  /** What enforcement will actually apply today. */
  effectiveEnabled: boolean;
  effectiveHrAdmin: number;
  effectiveChiefManager: number;
  effectiveEmployee: number;

  /** Company is on its own Azure OpenAI key, so it is never metered and any override is inert. */
  exemptByOwnKey: boolean;

  usedToday: number;
  activeUsersToday: number;
  resetsAtUtc: string;

  minLimit: number;
  maxLimit: number;
}

/**
 * FULL REPLACE, not a patch. Always send all four fields; `null` clears that override and returns
 * the tier to the platform default. Omitting a field would make "inherit again" unexpressible.
 */
export interface AiQuotaOverrideInput {
  enabled: boolean | null;
  hrAdmin: number | null;
  chiefManager: number | null;
  employee: number | null;
  note: string | null;
}

/** HR / Admin read-only projection. Carries no override detail and no operator note. */
export interface AiQuotaCompanyUsageView {
  companyId: number;
  metered: boolean;
  /** 'ownKey' | 'disabled' — render from an i18n key, never print raw. */
  exemptReason: string | null;

  hrAdminLimit: number;
  chiefManagerLimit: number;
  employeeLimit: number;

  /** 'hrAdmin' | 'chiefManager' | 'employee' — a token, rendered from an i18n key. */
  myTier: string;
  myLimit: number;
  myUsedToday: number;

  companyUsedToday: number;
  activeUsersToday: number;
  resetsAtUtc: string;
}

/** Daily AI quota for every company (Operator only). */
export async function getCompaniesAiQuota() {
  const response = await request.get<Result<AiQuotaConfigView[]>>("AISettings/Quota/Companies");
  return response.data;
}

/** Daily AI quota for one company (Operator only). */
export async function getCompanyAiQuota(companyId: number) {
  const response = await request.get<Result<AiQuotaConfigView>>(`AISettings/Quota/${companyId}`);
  return response.data;
}

/** Set the per-company daily AI quota override (Operator only). Full replace — see the input type. */
export async function updateCompanyAiQuota(companyId: number, input: AiQuotaOverrideInput) {
  const response = await request.put<Result<AiQuotaConfigView>>(`AISettings/Quota/${companyId}`, input);
  return response.data;
}

/**
 * The caller's own company's effective AI caps and today's consumption (HR / Admin).
 * READ ONLY BY DESIGN — there is no update counterpart, on the client or on the server.
 */
export async function getMyCompanyAiQuota() {
  const response = await request.get<Result<AiQuotaCompanyUsageView>>("AiQuota/MyCompany");
  return response.data;
}
